import { type OrgCompositeRankingResRelatedRankingResponseRelatedPagedData } from '@on3/api';
import { removeZeroTies } from '@on3/api/utils/games';
import { useSite } from '@on3/ui-lib/index';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { createUrl } from '@on3/ui-lib/utils/url';
import clsx from 'clsx';
import { On3Avatar } from 'components/Image/On3Avatar';
import Link from 'next/link';
import { createTitle } from 'utils/meta';

import styles from './TopRankings.module.scss';

interface TopRankingsProps {
  rankings: OrgCompositeRankingResRelatedRankingResponseRelatedPagedData;
  title?: string;
  isModule?: boolean;
}

export const TopRankings = ({
  title,
  rankings,
  isModule = false,
}: TopRankingsProps) => {
  const { sportsData = [] } = useSite();
  const { relatedModel, list = [] } = rankings;
  const { sportKey = 1 } = relatedModel?.ranking ?? {};

  const sport = sportsData.find((s) => s.key === sportKey);
  const sportName = sport?.name ?? 'Football';
  const sportSlug = sportName.toLowerCase();

  const state = relatedModel?.state;

  title =
    title ||
    createTitle(
      state?.name ? `${state.name} State High School` : 'High School',
      sportName,
      'Rankings',
    );

  if (!list.length) return null;

  return (
    <div className={clsx(styles.block, { [styles.module]: isModule })}>
      <h2>{title}</h2>
      <section className={styles.rankings}>
        {list.map((ranking, index) => (
          <Link
            className={styles.ranking}
            href={`/high-school/${ranking.organization?.urlSlug}/`}
            key={ranking.organizationKey}
          >
            <div className={styles.rank}>{index + 1}</div>
            <div className={styles.team}>
              <figure className={styles.avatar}>
                <On3Avatar
                  altText={`${ranking.organization?.name} ${ranking.organization?.mascot} Logo`}
                  domain={ranking.organization?.defaultAsset?.domain}
                  source={ranking.organization?.defaultAsset?.source}
                  teamAvatar
                />
              </figure>
              <div className={styles.info}>
                <dl className={styles.name}>
                  <dt>High School</dt>
                  <dd>
                    {ranking.organization?.knownAs ||
                      ranking.organization?.name}
                  </dd>
                </dl>
                <div className={styles.details}>
                  <dl className={styles.hometown}>
                    <dt>Location</dt>
                    <dd>
                      {`${ranking.city?.name}, ${ranking?.state?.abbreviation}`}
                    </dd>
                  </dl>
                  {!!state && (
                    <dl>
                      <dt>Composite Ranking</dt>
                      <dd>{`(#${ranking.compositeOverallRank})`}</dd>
                    </dl>
                  )}
                </div>
              </div>
            </div>
            <div className={styles.rating}>
              <span>{ranking.compositeScore?.toFixed(3)}</span>
              <span>{removeZeroTies(ranking.record)}</span>
            </div>
          </Link>
        ))}
      </section>
      <footer className={styles.footer}>
        <Link
          className={styles.viewAll}
          href={createUrl([
            'high-school',
            'rankings',
            sportSlug,
            state?.key ? slugify(state.name) : 'national',
          ])}
        >
          Complete Rankings
        </Link>
      </footer>
    </div>
  );
};
